<template>
	<div class="chat-container">
		<ul class="messages mb-5" ref="chat-messages">
			<li class="message" :class="message.role" v-for="message in messages" :key="message.response_id">
				<div class="message-item-wrapper" v-if="message.role === 'assistant'">
					<InlineSvg class="image" src="/images/icons/ic-stanley-profile.svg" />
					<div>
						<div class="para-2" v-html="textToShow(message)"></div>
						<div v-if="message.isStreaming" class="spinner">
							<div class="loader"></div>
						</div>
					</div>
				</div>
				<div class="message-item-wrapper" v-else>
					<img class="image" :src="user.profile_image" />
					<span class="para-2 preserve-linebreaks">{{ message.content }}</span>
				</div>
				<div
					class="d-flex flex-row justify-content-between"
					v-if="message.role === 'assistant' && message.response_id !== 'def' && !message.isStreaming"
				>
					<div class="feedback-wrapper" :class="{ 'has-feedback': message.feedback_status === -1 || message.feedback_status === 1 }">
						<button class="feedback-button" :class="{ selected: message.feedback_status === 1 }" @click="sendMessageFeedback(message, 1)">
							<InlineSvg class="no-feedback" src="/images/icons/ic-thumbs-up-outline.svg" />
							<InlineSvg class="chosen" src="/images/icons/ic-thumbs-up-solid.svg" />
						</button>
						<button
							class="feedback-button"
							:class="{ selected: message.feedback_status === -1 }"
							@click="sendMessageFeedback(message, -1)"
						>
							<InlineSvg class="no-feedback" src="/images/icons/ic-thumbs-down-outline.svg" />
							<InlineSvg class="chosen" src="/images/icons/ic-thumbs-down-solid.svg" />
						</button>
					</div>
					<button class="copy-button" @click="copyMessageContent(textToShow(message))">
						<InlineSvg class="no-feedback" src="/images/icons/ic-copy.svg" />
					</button>
				</div>
			</li>
			<li
				v-if="formSubmitting && progressBarValue > 0 && progressBarValue < 100"
				id="message-loading"
				class="py-5 d-flex justify-content-center align-items-center flex-column"
			>
				<h3 class="text-bold mb-4">Stanley is thinking...</h3>
				<AppProgress class="stanley-loading-bar" :value="progressBarValue" />
			</li>
		</ul>
		<div v-if="messages.length == 1" class="example-questions">
			<h3 class="d-md-flex d-none text-bold text-center align-items-center justify-content-center gap mb-5">
				<InlineSvg src="/images/icons/ic-lightning.svg" />Example Questions
			</h3>
			<h4 class="d-md-none text-bold text-center d-flex align-items-center justify-content-center mb-4">
				<InlineSvg src="/images/icons/ic-lightning.svg" />Example Questions
			</h4>
			<div class="question-chooser mb-4 mb-md-5">
				<button class="question-choice" v-for="(question, index) in exampleQuestions" :key="index" @click="initiateQuestion(question)">
					<span class="para-3 text-bold">{{ question }}</span>
					<InlineSvg class="question-arrow-image" src="/images/icons/ic-arrow-right.svg" />
				</button>
			</div>
			<div class="alpha-note para-3 mb-4 mb-md-5 d-flex flex-column gap-8px" v-if="showHelpSection">
				<span v-if="showHelpscoutLiveChat">
					Need more information about the platform? Visit our
					<a @click="clickMoreInformationLink('https://help.stan.store/')" class="mailto">Help Center</a> or chat with
					<a @click="openBeacon" class="mailto">support</a>
				</span>
				<span v-else>
					Need more information about the platform? Visit our
					<a @click="clickMoreInformationLink('https://help.stan.store/')" class="mailto">Help Center</a> or email
					<a @click="openBeacon" class="mailto">support</a>
				</span>
			</div>
		</div>
		<form id="question-form" class="question-form" method="post" @submit.prevent="handleSubmit">
			<div class="question-input-wrapper d-flex justify-content-center align-items-center" :class="{ disabled: formSubmitting }">
				<textarea
					v-model="inputQuestion"
					maxlength="800"
					id="ai-stanley-ask-prompt-textarea"
					class="col pr-1 stanley-ask-prompt-textarea"
					placeholder="Ask Stanley..."
					:disabled="formSubmitting"
				></textarea>
				<button type="submit" class="btn-transparent col-auto" :disabled="formSubmitting">
					<InlineSvg src="/images/icons/ic-send.svg" />
				</button>
			</div>
		</form>
	</div>
</template>

<script>
	import { mapGetters } from 'vuex'

	export default {
		name: 'StanleyChatVisual',
		props: {
			messages: {
				type: Array,
				required: true,
			},
			formSubmitting: {
				type: Boolean,
				required: true,
			},
			progressBarValue: {
				type: Number,
				required: true,
			},
			initialPromptQuestion: {
				type: String,
				default: '',
			},
			exampleQuestions: {
				type: Object,
				required: true,
			},
			showHelpSection: {
				type: Boolean,
				default: true,
			},
			isStreaming: {
				type: Boolean,
				default: false,
			},
		},
		data() {
			return {
				inputQuestion: '',
			}
		},
		mounted() {
			// Add event listenr on text area to submit on enter
			document.getElementById('ai-stanley-ask-prompt-textarea').addEventListener('keypress', e => {
				if (e.key === 'Enter' && !e.shiftKey) {
					e.preventDefault()
					this.handleSubmit()
				}
			})
		},
		computed: {
			...mapGetters('Auth', ['user']),
			showHelpscoutLiveChat() {
				return this.user.experiments?.support_email_vs_helpscout_live_chat
			},
		},
		methods: {
			textToShow(message) {
				if (message.content_html) {
					return message.content_html
				}
				return message.content
			},
			copyMessageContent(content) {
				const tempElement = document.createElement('div')
				tempElement.innerHTML = content
				const plainText = tempElement.textContent || tempElement.innerText || ''
				this.$commonHelper.copyToClipboard(plainText, this.clipboardSuccessHandler)
			},
			clipboardSuccessHandler() {
				this.$stanNotify({
					type: 'success',
					title: this.$t('Done!'),
					text: this.$t('Copied to clipboard'),
					duration: 5000,
				})
			},
			handleSubmit() {
				if (this.inputQuestion.trim()) {
					this.$emit('sendMessage', this.inputQuestion)
					this.inputQuestion = ''
				}
			},
			initiateQuestion(question) {
				this.$emit('sendMessage', question)
			},
			sendMessageFeedback(message, feedback) {
				this.$emit('sendMessageFeedback', { message, feedback })
			},
			clickMoreInformationLink(url) {
				window.open(url, '_blank')
				this.$stanAnalytics('stanley-more-information-link-click', {
					meta: { user_id: this.user.user_id, username: this.user.username },
					props: { link: url },
				})
			},
			openBeacon() {
				if (window.Beacon) {
					// We can only intailize the beacon once
					if (!window?.helpscoutBeaconInitialized) {
						window.helpscoutBeaconInitialized = true
						if (this.showHelpscoutLiveChat) {
							// Live chat - will only show once they click support here
							window.Beacon('init', 'e34bbca1-5d5c-4c8f-a20c-115e9388ea99')
						} else {
							// Standard email support
							window.Beacon('init', '9a7c610f-db35-42bf-894c-3193358bdd85')
						}
					}

					// Goes to respective chat page
					if (this.showHelpscoutLiveChat) {
						window.Beacon('navigate', '/ask/chat/')
					} else {
						window.Beacon('navigate', '/ask/message')
					}

					window.Beacon('open')

					this.$stanAnalytics('open-helpscout-beacon', {
						meta: { user_id: this.user.user_id, username: this.user.username },
						props: {
							beacon_type: this.showHelpscoutLiveChat ? 'live-chat' : 'email',
						},
					})
				} else {
					this.clickMoreInformationLink('mailto:friends@stanwith.me')
				}
			},
		},
	}
</script>

<style lang="scss" scoped>
	.spinner {
		filter: drop-shadow(-2px 4px 4px rgba(0, 0, 0, 0.12));
		margin: 20px 0;
		transform-origin: center center;
		width: fit-content;
		animation: bouncer 0.5s infinite alternate-reverse ease-in-out;
	}
	.loader {
		width: 17px;
		aspect-ratio: 1;
		border-radius: 50%;
		background: #6355ff;
		display: grid;
		box-shadow: inset -2px -2px 8px rgba(0, 0, 0, 0.1), inset 1px 1px 3px rgba(255, 255, 255, 0.5);
		animation: l22-0 2s infinite linear;
	}
	.loader:before,
	.loader:after {
		content: '';
		grid-area: 1/1;
		margin: 15%;
		border-radius: 50%;
		background: inherit;
		transform: rotate(0deg) translate(150%);
		animation: l22 1s infinite;
	}
	.loader:after {
		animation-delay: -0.5s;
	}
	@keyframes l22-0 {
		100% {
			transform: rotate(1turn) scale(1);
		}
	}
	@keyframes l22 {
		100% {
			transform: rotate(1turn) translate(150%);
		}
	}
	@keyframes bouncer {
		0% {
			transform: scale(1);
		}
		100% {
			transform: scale(0.8);
		}
	}

	.message::v-deep {
		h1,
		h2,
		h3,
		h4,
		h5,
		h6 {
			margin-top: 0.5em;
			margin-bottom: 0.3em;
		}
		ul,
		ol {
			margin-top: 0.2em;
			margin-bottom: 2em;
			padding-left: 1em;
		}
	}
	@keyframes float {
		from {
			opacity: 0;
			transform: translateY(20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
	.chat-container {
		margin: auto;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		overflow: scroll;
		/* Hide scrollbar for IE, Edge and Firefox */
		-ms-overflow-style: none; /* IE and Edge */
		scrollbar-width: none; /* Firefox */

		/* Hide scrollbar for Chrome, Safari and Opera */
		&::-webkit-scrollbar {
			display: none;
		}
		.messages {
			width: 100%;
			max-width: 900px;
			flex: 0 0 auto;
			list-style: none;
			padding: 0;
			margin-left: auto;
			margin-right: auto;

			.message {
				animation: float 0.3s ease;
				margin-bottom: 15px;

				padding: 20px;
				border-radius: 15px;

				@media (min-width: 1200px) {
					margin-left: -25px;
				}
				.message-item-wrapper {
					display: flex;
					gap: 15px;
					align-items: center;
					width: 100%;
					@media (min-width: 1200px) {
						gap: 20px;
					}
				}
				.feedback-wrapper {
					margin-top: 5px;
					.feedback-button {
						svg {
							display: none;
							width: 20px;
							height: 20px;
						}
						.no-feedback {
							display: block;
						}
					}
					&.has-feedback {
						.feedback-button {
							cursor: unset;
							.no-feedback {
								opacity: 0.5;
							}
							&.selected {
								.chosen {
									display: block;
								}
								.no-feedback {
									display: none;
								}
							}
						}
					}
				}
				.image {
					width: 24px;
					height: 24px;
					border-radius: 100%;
					flex: 0 0 auto;
					align-self: flex-start;
					@media (min-width: 1200px) {
						width: 28px;
						height: 28px;
					}
				}
				&.assistant {
					background-color: #f5f5ff;
					display: flex;
					flex-direction: column;
					align-items: flex-end;
					.message-item-wrapper {
						span {
							white-space: pre-line;
							line-height: 1.5;
						}
					}
				}
				&.user {
					background-color: var(--stan-gray-soft-color);
				}
			}
		}
		.example-questions {
			flex: 0 0 auto;
			height: auto;
			max-width: 500px;
			margin: auto;
			h4,
			h3 {
				gap: 8px;
				svg {
					width: 20px;
					height: 20px;
				}
			}
			.question-chooser {
				display: flex;
				flex-wrap: wrap;
				gap: 15px;
				.question-choice {
					padding: 15px 20px;
					width: calc(50% - 7.5px);
					background-color: var(--stan-gray-primary-color);
					border-radius: 10px;
					display: flex;
					gap: 5px;
					text-align: left;
					color: var(--stan-text-dark-color);
					flex-direction: column;
					justify-content: space-between;
					@media (min-width: 1200px) {
						justify-content: flex-start;
						gap: 10px;
						align-items: flex-end;
						height: fit-content;
						padding: 20px 25px;
						flex-direction: row;
					}
					.question-arrow-image {
						flex: 0 0 auto;
						width: 16px;
						height: 16px;
						align-self: flex-end;
					}
					&:hover {
						background-color: var(--stan-gray-strike-color);
					}
				}
			}
			.alpha-note {
				background-color: var(--stan-gray-soft-color);
				color: var(--stan-text-light-color);
				padding: 20px;
				border-radius: 10px;
				width: 85%;
				margin: auto;
			}
		}
	}
	.question-form {
		bottom: 10px;
		@media (min-width: 992px) {
			bottom: 30px;
		}
		@media (min-width: 1200px) {
			position: absolute;
			left: 50%;
			transform: translateX(-50%);
			width: calc(100% - 50px);

			width: 100%;
			max-width: 925px;
			transform: translateX(calc(-50% - 12.5px));
		}

		.question-input-wrapper {
			background-color: var(--stan-gray-light-color);
			border-radius: 10px;
			padding: 10px;
			@media (min-width: 1200px) {
				padding: 10px;
			}
			&.disabled {
				color: var(--stan-text-light-color) !important;
				background: var(--stan-primary-light-color) !important;
			}
		}
		.btn-transparent {
			background: none;
			border: none;
			box-shadow: none;
			border-radius: none;
		}
	}

	@keyframes dots {
		0%,
		20% {
			color: rgba(0, 0, 0, 0);
			text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0), 0.5em 0 0 rgba(0, 0, 0, 0);
		}
		40% {
			color: var(--stan-text-light-color);
			text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0), 0.5em 0 0 rgba(0, 0, 0, 0);
		}
		60% {
			text-shadow: 0.25em 0 0 var(--stan-text-light-color), 0.5em 0 0 rgba(0, 0, 0, 0);
		}
		80%,
		100% {
			text-shadow: 0.25em 0 0 var(--stan-text-light-color), 0.5em 0 0 var(--stan-text-light-color);
		}
	}
	.stanley-loading-bar {
		width: 100%;
		max-width: 300px;
		height: 10px;
	}
	.stanley-ask-prompt-textarea {
		resize: none;
		height: 70px;
		&:focus {
			outline: none;
		}
		&:disabled {
			cursor: not-allowed;
			color: var(--stan-text-light-color) !important;
			background: var(--stan-primary-light-color) !important;
			-webkit-text-fill-color: var(--stan-text-light-color);
			-webkit-opacity: 1;
		}
	}
	.preserve-linebreaks {
		white-space: pre-line;
	}
</style>
